import './LeaderboardJolavanilleArenaSolo.css';
import React, { useState, useEffect, useRef, useMemo } from "react"
import { useLocation } from 'react-router-dom';
import { parseExcludedSessionIds } from '../../shared/leaderboardShared';
import {
    loadPlayerCountriesByQuery,
    PLAYER_COUNTRY_FALLBACK_FLAG_URL,
} from '../../shared/playerCountryApi';

function getEntryMemberNames(entry) {
    const names = Array.isArray(entry?.members)
        ? entry.members
        : [entry?.name, entry?.name2];
    return names.map((name) => String(name || '').trim()).filter(Boolean);
}

const Row = React.memo(function Row({ rank, teamname, points, totalVictories, totalDamageDone, totalDamageTaken, wins, games, order, showGamesColumn, onClick, positionChange, refreshDelta, showPositionIndicators, animationEnabled, hasPositionChanged, cascadeFadeEnabled, cascadeIndex, alive, showFlags, memberData, isCompactLayout, highlighted }) {
    const renderPositionChange = () => {

        if (!showPositionIndicators || alive || games < 2 || positionChange === null) {
            return null;
        }

        const getIndicatorStyle = (type, value) => {
            const textLength = String(value).length;
            let baseWidth, fontSize, padding;
            if (textLength === 1) {
                baseWidth = 20;
                fontSize = 11;
                padding = '2px 6px';
            } else if (textLength === 2) {
                baseWidth = 26;
                fontSize = 10;
                padding = '2px 5px';
            } else if (textLength === 3) {
                baseWidth = 32;
                fontSize = 9;
                padding = '2px 4px';
            } else {
                baseWidth = 38;
                fontSize = 8;
                padding = '2px 3px';
            }

            const baseStyle = {
                padding: padding,
                borderRadius: '3px',
                fontSize: `${fontSize}px`,
                fontWeight: 'bold',
                border: '1px solid',
                minWidth: `${baseWidth}px`,
                textAlign: 'center',
                display: 'inline-block',
                marginLeft: '0px',
                position: 'absolute',
                right: isCompactLayout ? '-20px' : '-36px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
            };

            if (type === 'neutral') {
                return {
                    ...baseStyle,
                    backgroundColor: '#666',
                    color: '#fff',
                    borderColor: '#666'
                };
            } else if (type === 'positive') {
                return {
                    ...baseStyle,
                    backgroundColor: '#4CAF50',
                    color: '#fff',
                    borderColor: '#4CAF50'
                };
            } else {
                return {
                    ...baseStyle,
                    backgroundColor: '#f44336',
                    color: '#fff',
                    borderColor: '#f44336'
                };
            }
        };


        if (positionChange === 0) {
            return <span className="position_change neutral" style={getIndicatorStyle('neutral', '=')}>=</span>;
        }
        if (positionChange > 0) {
            return <span className="position_change positive" style={getIndicatorStyle('positive', `+${positionChange}`)}>+{positionChange}</span>;
        } else {
            return <span className="position_change negative" style={getIndicatorStyle('negative', positionChange)}>{positionChange}</span>;
        }
    };

    const getAnimationStyle = () => {
        if (!animationEnabled || !hasPositionChanged || !refreshDelta) return {};

        const rowHeight = 60;
        const realDistance = Math.abs(refreshDelta) * rowHeight;

        const baseSpeed = 200;
        const minDuration = 0.6;
        const maxDuration = 2.0;

        let calculatedDuration = realDistance / baseSpeed;
        calculatedDuration = Math.max(minDuration, Math.min(maxDuration, calculatedDuration));

        const fromPosition = refreshDelta > 0 ? realDistance : -realDistance;

        return {
            '--slide-from': `${fromPosition}px`,
            '--slide-to': '0px',
            animation: `slideFromTo ${calculatedDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10
        };
    };

    const hasFlagMembers = showFlags && memberData && memberData.length > 0;
    const estimatedLabelLength = hasFlagMembers
        ? memberData.reduce((sum, member) => sum + (member?.name?.length || 0), 0) + ((memberData.length - 1) * 3) + (memberData.length * 4) + (alive ? 2 : 0)
        : teamname.length + (alive ? 2 : 0);

    const getAutoFontSize = (length) => {
        if (isCompactLayout) {
            if (length > 72) return 9;
            if (length > 64) return 10;
            if (length > 56) return 11;
            if (length > 48) return 12;
            if (length > 42) return 13;
            if (length > 36) return 14;
            if (length > 30) return hasFlagMembers ? 16 : 17;
            if (length > 24) return hasFlagMembers ? 18 : 19;
            if (length > 18) return hasFlagMembers ? 20 : 21;
            if (length > 14) return hasFlagMembers ? 21 : 22;
            return hasFlagMembers ? 23 : 24;
        }

        if (length <= 18) return 26;
        if (length <= 24) return 24;
        if (length <= 30) return 22;
        if (length <= 36) return 20;
        if (length <= 44) return 18;
        if (length <= 54) return 16;
        if (length <= 64) return 14;
        return 12;
    };

    const teamFontSize = getAutoFontSize(estimatedLabelLength);
    const flagSize = Math.max(isCompactLayout ? 10 : 12, Math.min(isCompactLayout ? 18 : 22, teamFontSize + (isCompactLayout ? 1 : 2)));

    return (
        <div className={`row_container${highlighted ? ' row_highlighted' : ''}`} style={{
            '--animation-order': order,
            opacity: cascadeFadeEnabled ? 0 : (animationEnabled && hasPositionChanged ? 0.9 : 1),
            animation: cascadeFadeEnabled ? 'fadeIn 0.8s forwards' : 'none',
            animationDelay: cascadeFadeEnabled ? `${cascadeIndex * 0.1}s` : '0s',
            transition: animationEnabled && hasPositionChanged ? 'none' : 'opacity 0.3s ease',
            ...getAnimationStyle()
        }}>
            <div className='rank_container' style={{
                fontSize: rank >= 1000 ? '24px' : rank >= 100 ? '24px' : '26px',
                paddingLeft: rank >= 1000 ? '16px' : rank >= 100 ? '12px' : rank >= 10 ? '4px' : '0px',
                paddingRight: '8px',

            }}>
                {rank}
                {renderPositionChange()}
            </div>
            <div className='name_container' style={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: isCompactLayout ? 'hidden' : 'visible',
                textOverflow: isCompactLayout ? 'ellipsis' : 'clip',
                paddingLeft: isCompactLayout ? '12px' : undefined,
                paddingRight: isCompactLayout ? '12px' : undefined,
                boxSizing: isCompactLayout ? 'border-box' : undefined,

            }} onClick={onClick}>
                <span className='team_name' style={{ fontSize: `${teamFontSize}px` }}>
                    {alive && <span className='alive-dot' style={{
                        width: '15px',
                        height: '15px',
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        boxShadow: '0 0 14px red',
                        display: 'inline-block',
                        flexShrink: 0,
                        position: 'absolute',
                        left: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                    }} />}
                    {showFlags && memberData && memberData.length > 0 ? (
                        memberData.map((member, idx) => (
                            <span key={idx} className='member_with_flag'>
                                <img
                                    src={member.flagUrl}
                                    alt="flag"
                                    className='flag_icon'
                                    style={{ width: `${flagSize}px`, height: `${flagSize}px` }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {idx < memberData.length - 1 && <span className='separator'> - </span>}
                            </span>
                        ))
                    ) : (
                        teamname
                    )}
                </span>
            </div>
            <div className='info_box'>{wins}</div>
            <div className='info_box'>{totalVictories}</div>
            <div className='info_box'>{totalDamageDone}</div>
            <div className='info_box'>{totalDamageTaken}</div>
            <div className='info_box'>{points}</div>
        </div>
    );
}, (prevProps, nextProps) => {

    return (
        prevProps.rank === nextProps.rank &&
        prevProps.teamname === nextProps.teamname &&
        prevProps.points === nextProps.points &&
        prevProps.totalVictories === nextProps.totalVictories &&
        prevProps.totalDamageDone === nextProps.totalDamageDone &&
        prevProps.totalDamageTaken === nextProps.totalDamageTaken &&
        prevProps.wins === nextProps.wins &&
        prevProps.games === nextProps.games &&
        prevProps.showGamesColumn === nextProps.showGamesColumn &&
        prevProps.positionChange === nextProps.positionChange &&
        prevProps.refreshDelta === nextProps.refreshDelta &&
        prevProps.showPositionIndicators === nextProps.showPositionIndicators &&
        prevProps.animationEnabled === nextProps.animationEnabled &&
        prevProps.hasPositionChanged === nextProps.hasPositionChanged &&
        prevProps.cascadeFadeEnabled === nextProps.cascadeFadeEnabled &&
        prevProps.alive === nextProps.alive &&
        prevProps.showFlags === nextProps.showFlags &&
        prevProps.memberData === nextProps.memberData &&
        prevProps.isCompactLayout === nextProps.isCompactLayout &&
        prevProps.highlighted === nextProps.highlighted
    );
});

function Leaderboardjolavanille_arena_solo() {

    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const leaderboard_id = urlParams.get('id');
    const cascadeParam = urlParams.get('cascade');
    const flagsParam = urlParams.get('flags');
    const excludedSessionIds = useMemo(() => parseExcludedSessionIds(new URLSearchParams(location.search)), [location.search]);
    const excludedSessionIdsKey = useMemo(() => Array.from(excludedSessionIds).sort().join(','), [excludedSessionIds]);

    const [leaderboard, setLeaderboard] = useState([]);
    const [apiPage, setApiPage] = useState(0);
    const [localPage, setLocalPage] = useState(0);
    const [totalApiPages, setTotalApiPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFlags, setShowFlags] = useState(flagsParam === 'true');
    const [showSearch, setShowSearch] = useState(true);
    const [activeGroup, setActiveGroup] = useState('A');


    const [showGamesColumn, setShowGamesColumn] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamDetails, setTeamDetails] = useState({});
    const [previousPositions, setPreviousPositions] = useState({});
    const [lastChangeTime, setLastChangeTime] = useState(Date.now());
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [hasRefreshedOnce, setHasRefreshedOnce] = useState(false);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(cascadeParam === 'true');
    const [previousLeaderboard, setPreviousLeaderboard] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [tiebreakers, setTiebreakers] = useState([]);
    const previousLeaderboardRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const wasAllDeadRef = useRef(false);


    const getApiUrlForGroup = (group) => {
        const base = 'https://jolavanille-leaderboard-api.vercel.app/api/leaderboard?format=solo';
        if (group === 'finale') return `${base}&session=2&group=all`;
        if (group === 'A') return `${base}&session=1&group=A`;
        if (group === 'B') return `${base}&session=1&group=B`;
        return `${base}&session=1&group=all`;
    };

    const loadLeaderboard = async () => {
        try {
            const url = getApiUrlForGroup(activeGroup);
            const response = await fetch(url);
            const data = await response.json();
            const entries = Array.isArray(data.entries) ? data.entries : [];
            const playerCountriesByName = showFlags
                ? await loadPlayerCountriesByQuery(entries.flatMap(getEntryMemberNames))
                : {};

            const baseMapped = entries.map(entry => {
                const memberNames = getEntryMemberNames(entry);
                return {
                    teamname: entry.teamName || entry.name || memberNames.join(' - '),
                    wins: entry.wins || 0,
                    totalVictories: entry.totalVictories || 0,
                    totalDamageDone: entry.totalDamageDone || 0,
                    totalDamageTaken: entry.totalDamageTaken || 0,
                    games: entry.games || 0,
                    place: entry.rank,
                    points: entry.points || 0,
                    alive: false,
                    memberData: showFlags ? memberNames.map((name) => {
                        const country = playerCountriesByName[name];
                        return {
                            name,
                            countryName: country?.countryName || 'Global',
                            flagUrl: country?.flagUrl || PLAYER_COUNTRY_FALLBACK_FLAG_URL,
                            fallbackFlagUrl: PLAYER_COUNTRY_FALLBACK_FLAG_URL,
                        };
                    }) : [],
                    teamId: entry.id || entry.teamName,
                };
            });

            const maxGames = baseMapped.reduce((m, e) => Math.max(m, e.games), 0);
            const allSameGames = baseMapped.length > 0 && baseMapped.every(e => e.games === maxGames);

            const snapshotsKey = `jolavanille_arena_solo_snapshots_${activeGroup}`;
            const snapshots = JSON.parse(localStorage.getItem(snapshotsKey) || '{}');
            if (maxGames > 0) {
                snapshots[maxGames] = snapshots[maxGames] || {};
                baseMapped.forEach(e => { snapshots[maxGames][e.teamId] = e.place; });
                localStorage.setItem(snapshotsKey, JSON.stringify(snapshots));
            }

            const prevSnapshot = maxGames > 1 ? snapshots[maxGames - 1] : null;
            const showIndicators = allSameGames && maxGames > 1 && prevSnapshot;

            const mapped = baseMapped.map(e => {
                const prevPlace = prevSnapshot ? prevSnapshot[e.teamId] : null;
                const positionChange = (showIndicators && prevPlace != null) ? (prevPlace - e.place) : null;
                return {
                    ...e,
                    positionChange,
                    hasPositionChanged: positionChange !== null && positionChange !== 0,
                };
            });

            const details = {};
            entries.forEach(entry => {
                const key = entry.teamName || entry.name || (entry.members || []).join(' - ');
                details[key] = entry;
            });

            const previousLeaderboard = previousLeaderboardRef.current;
            const previousPlaces = new Map(
                (previousLeaderboard || []).map((team) => [team.teamId, team.place])
            );
            const withRefreshDeltas = mapped.map((team) => {
                const previousPlace = previousPlaces.get(team.teamId);
                const refreshDelta = typeof previousPlace === 'number' ? previousPlace - team.place : 0;
                return {
                    ...team,
                    refreshDelta,
                    hasPositionChanged: refreshDelta !== 0,
                };
            });

            setLeaderboard(withRefreshDeltas);
            previousLeaderboardRef.current = withRefreshDeltas;

            if (previousLeaderboard && withRefreshDeltas.some((team) => team.refreshDelta !== 0)) {
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }
            setShowGamesColumn(false);
            setShowPositionIndicators(!!showIndicators);
            setHasRefreshedOnce(true);
            setTotalApiPages(1);
            setTeamDetails(details);
            setTiebreakers(Array.isArray(data.scoring?.tiebreakers) ? data.scoring.tiebreakers : []);

            if (isInitialLoadRef.current) {
                setIsInitialLoad(false);
                isInitialLoadRef.current = false;
            }
        } catch (error) {
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    useEffect(() => {
        loadLeaderboard();

        const interval = setInterval(loadLeaderboard, 15000);

        return () => clearInterval(interval);
    }, [leaderboard_id, showFlags, excludedSessionIdsKey, activeGroup]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch(prev => !prev);
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setLocalPage(0);
    }, [searchQuery]);

    useEffect(() => {
        setLeaderboard([]);
        previousLeaderboardRef.current = null;
        setCascadeFadeEnabled(true);
        const t = setTimeout(() => setCascadeFadeEnabled(false), 2500);
        return () => clearTimeout(t);
    }, [activeGroup]);



    const handleTeamClick = (teamname) => {
        setSelectedTeam(teamname);
    };

    const closeModal = () => {
        setSelectedTeam(null);
    };



    function nextPage() {
        const total = getFilteredLeaderboard().length;
        const maxPages = Math.max(0, Math.ceil(total / 16) - 1);

        if (localPage < maxPages) {
            setLocalPage(localPage + 1);
        }
    }

    function previousPage() {
        if (localPage > 0) {
            setLocalPage(localPage - 1);
        }
    }

    useEffect(() => {
        const supportSinglePage = totalApiPages === 1;
        setShowGamesColumn(supportSinglePage);
    }, [totalApiPages]);

    const getFilteredLeaderboard = () => {
        if (!searchQuery) return leaderboard;
        return leaderboard.filter(team => {
            if (team.teamname.toLowerCase().includes(searchQuery.toLowerCase())) {
                return true;
            }
            if (!isNaN(searchQuery) && searchQuery.trim() !== '') {
                const searchPosition = parseInt(searchQuery.trim());
                if (team.place === searchPosition) {
                    return true;
                }
            }
            if (teamDetails[team.teamname] && teamDetails[team.teamname].members) {
                return teamDetails[team.teamname].members.some(member =>
                    member.ingame_name && member.ingame_name.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            return false;
        });
    };

    const filteredLeaderboard = getFilteredLeaderboard();
    const currentPage = Math.floor(apiPage + localPage);
    const leftItems = filteredLeaderboard.slice(currentPage * 16, (currentPage * 16) + 8);
    const rightItems = filteredLeaderboard.slice((currentPage * 16) + 8, (currentPage * 16) + 16);
    const isSingleColumnLayout = rightItems.length === 0;
    const displayedLeaderboard = leftItems;
    const nextPageFromPoints = nextPage;
    const nextPageFromGames = nextPage;

    const renderHeader = () => (
        <div className='header_container'>
            <div className='rank_header' onClick={previousPage}>PLACE</div>
            <div className='name_header'>ÉQUIPES</div>
            <div className='info_header'>WINS</div>
            <div className='info_header'>VICTOIRES</div>
            <div className='info_header'>DMG DONE</div>
            <div className='info_header'>DMG TAKEN</div>
            <div className='info_header' onClick={nextPage}>PTS</div>
        </div>
    );

    const renderRows = (items, cascadeOffset = 0, isCompactLayout = !isSingleColumnLayout) => items.map((data, index) => {
        const positionChange = Math.abs(data.positionChange || 0);
        let animationOrder;

        if (positionChange >= 500) {
            animationOrder = 1;
        } else if (positionChange >= 100) {
            animationOrder = 2;
        } else if (positionChange >= 50) {
            animationOrder = 3;
        } else if (positionChange >= 10) {
            animationOrder = 4;
        } else if (positionChange > 0) {
            animationOrder = 5;
        } else {
            animationOrder = index + 6;
        }

        return (
            <Row
                key={`${data.teamId || data.teamname}-${data.place}`}
                rank={data.place}
                teamname={data.teamname}
                points={data.points}
                totalVictories={data.totalVictories}
                totalDamageDone={data.totalDamageDone}
                totalDamageTaken={data.totalDamageTaken}
                wins={data.wins}
                games={data.games}
                order={animationOrder}
                showGamesColumn={showGamesColumn}
                onClick={() => handleTeamClick(data.teamname)}
                positionChange={data.positionChange}
                refreshDelta={data.refreshDelta || 0}
                showPositionIndicators={showPositionIndicators}
                animationEnabled={animationEnabled && data.hasPositionChanged}
                hasPositionChanged={data.hasPositionChanged || false}
                cascadeFadeEnabled={cascadeFadeEnabled}
                cascadeIndex={cascadeOffset + index}
                alive={data.alive}
                showFlags={showFlags}
                memberData={data.memberData}
                isCompactLayout={isCompactLayout}
                highlighted={activeGroup === 'finale' ? data.place <= 4 : data.place <= 8}
            />
        );
    });

    return (
        <div className='jolavanille_arena_solo'>
            <div className='leaderboard_container'>


                <div className='leaderboard_title' style={{
                    fontFamily: 'Eurostile',
                    fontSize: '32px',
                    color: '#ffffffff',
                    textAlign: 'center',
                    paddingTop: '50px',
                    marginBottom: '20px',
                    fontWeight: 'bold'
                }}>Classement Jolavanille Arena Solo</div>

                <div className={`dual_leaderboard ${isSingleColumnLayout ? 'single-column' : 'two-columns'}`} key={`board-${activeGroup}`}>
                    <div className='leaderboard_column'>
                        {renderHeader()}
                        {renderRows(leftItems, 0, !isSingleColumnLayout)}
                    </div>
                    {!isSingleColumnLayout && (
                        <div className='leaderboard_column second-column'>
                            {renderHeader()}
                            {renderRows(rightItems, 8, true)}
                        </div>
                    )}
                </div>

                <div className='group_buttons'>
                    <button
                        className={`group_button ${activeGroup === 'A' ? 'active' : ''}`}
                        onClick={() => setActiveGroup('A')}
                    >Classement Groupe A</button>
                    <button
                        className={`group_button ${activeGroup === 'B' ? 'active' : ''}`}
                        onClick={() => setActiveGroup('B')}
                    >Classement Groupe B</button>
                    <button
                        className={`group_button ${activeGroup === 'finale' ? 'active' : ''}`}
                        onClick={() => setActiveGroup('finale')}
                    >Classement Finale Solo</button>
                </div>

                {selectedTeam && teamDetails[selectedTeam] && (
                    <div className='modal_overlay' onClick={closeModal}>
                        <div className='modal_content' onClick={(e) => e.stopPropagation()}>
                            <div className='modal_header'>
                                <h2>Stats détaillées - {selectedTeam}</h2>
                                <button className='close_button' onClick={closeModal}>×</button>
                            </div>
                            <div className='modal_body'>
                                {(() => {
                                    const entry = teamDetails[selectedTeam];
                                    const tiebreakerLabels = {
                                        wins: 'Wins (Top 1)',
                                        victories: 'Total victoires',
                                        damageDone: 'Total Damage Done',
                                        damageTaken: 'Total Damage Taken',
                                        avgElims: 'Élims moyennes',
                                        avgPlacement: 'Place moyenne',
                                    };
                                    const tiebreakerFields = {
                                        wins: entry.wins,
                                        victories: entry.totalVictories,
                                        damageDone: entry.totalDamageDone,
                                        damageTaken: entry.totalDamageTaken,
                                        avgElims: (entry.avgElims || 0).toFixed ? Number(entry.avgElims).toFixed(2) : entry.avgElims,
                                        avgPlacement: (entry.avgPlacement || 0).toFixed ? Number(entry.avgPlacement).toFixed(2) : entry.avgPlacement,
                                    };
                                    return (
                                        <>
                                            <div className='team_summary'>
                                                <h3>Résumé de l'équipe :</h3>
                                                <div className='team_stats'>
                                                    <div className='stat_item'>
                                                        <span className='stat_label'>Position:</span>
                                                        <span className='stat_value'>#{entry.rank}</span>
                                                    </div>
                                                    <div className='stat_item'>
                                                        <span className='stat_label'>Points actuels :</span>
                                                        <span className='stat_value'>{entry.points}</span>
                                                    </div>
                                                    <div className='stat_item'>
                                                        <span className='stat_label'>Parties jouées :</span>
                                                        <span className='stat_value'>{entry.games}</span>
                                                    </div>
                                                    {tiebreakers.map((tb, i) => (
                                                        <div key={i} className='stat_item'>
                                                            <span className='stat_label'>{tiebreakerLabels[tb.key] || tb.key}{tb.enabled === false ? ' (désactivé)' : ''} :</span>
                                                            <span className='stat_value'>{tiebreakerFields[tb.key] ?? '-'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {entry.members && entry.members.length > 0 && (
                                                <div className='members_section'>
                                                    <h3>Membre(s) de l'équipe :</h3>
                                                    <div className='members_list'>
                                                        {entry.members.map((member, index) => (
                                                            <div key={index} className='member_item'>
                                                                <span className='member_name'>{member}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {Array.isArray(entry.matches) && entry.matches.length > 0 && (
                                                <div className='sessions_section'>
                                                    <h3>Historique détaillé des games :</h3>
                                                    <div className='sessions_table'>
                                                        <div className='session_header'>
                                                            <div>Game</div>
                                                            <div>Place</div>
                                                            <div>Élims</div>
                                                            <div>Dmg Done</div>
                                                            <div>Dmg Taken</div>
                                                            <div>Points</div>
                                                        </div>
                                                        {entry.matches.map((m, index) => (
                                                            <div key={index} className='session_row'>
                                                                <div className='session_highlight'>{m.label || `Game ${index + 1}`}</div>
                                                                <div className={`place_${m.placement <= 3 ? 'top' : m.placement <= 10 ? 'good' : 'normal'}`}>{m.placement}</div>
                                                                <div className='session_highlight'>{m.elims}</div>
                                                                <div className='session_highlight'>{m.damageDone}</div>
                                                                <div className='session_highlight'>{m.damageTaken}</div>
                                                                <div className='session_highlight'>{m.points}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'none' }}>
                    {false && displayedLeaderboard.map((data, index) => {
                        const positionChange = Math.abs(data.positionChange || 0);
                        let animationOrder;

                        if (positionChange >= 500) {
                            animationOrder = 1;
                        } else if (positionChange >= 100) {
                            animationOrder = 2;
                        } else if (positionChange >= 50) {
                            animationOrder = 3;
                        } else if (positionChange >= 10) {
                            animationOrder = 4;
                        } else if (positionChange > 0) {
                            animationOrder = 5;
                        } else {
                            animationOrder = index + 6;
                        }

                        return null;
                    })}

                    {false && selectedTeam && teamDetails[selectedTeam] && (
                        <div className='modal_overlay' onClick={closeModal}>
                            <div className='modal_content' onClick={(e) => e.stopPropagation()}>
                                <div className='modal_header'>
                                    <h2>Stats - {selectedTeam}</h2>
                                    <button className='close_button' onClick={closeModal}>Ã—</button>
                                </div>
                                <div className='modal_body'>
                                    <div className='team_summary'>
                                        <h3>RÃ©sumÃ© de l'Ã©quipe :</h3>
                                        <div className='team_stats'>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Position:</span>
                                                <span className='stat_value'>#{teamDetails[selectedTeam].teamData.place}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Points actuels :</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].teamData.points}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Parties jouÃ©es :</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Victoires:</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place === 1).length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Top 3:</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place <= 3).length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Ã‰lims totales:</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Place moyenne:</span>
                                                <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.place, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Ã‰lims/partie:</span>
                                                <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Meilleure place:</span>
                                                <span className='stat_value'>{Math.min(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Pire place:</span>
                                                <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Max Ã©lims/partie:</span>
                                                <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.kills))}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='members_section'>
                                        <h3>Membre(s) de l'Ã©quipe :</h3>
                                        <div className='members_list'>
                                            {teamDetails[selectedTeam].members.map((member, index) => (
                                                <div key={index} className='member_item'>
                                                    <span className='member_name'>{member.name}</span>
                                                    {member.ingame && <span className='member_ingame'>{member.ingame}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='sessions_section'>
                                        <h3>Historique dÃ©taillÃ© des games :</h3>
                                        <div className='sessions_table'>
                                            <div className='session_header'>
                                                <div>Game</div>
                                                <div>Place</div>
                                                <div>Ã‰liminations</div>
                                            </div>
                                            {teamDetails[selectedTeam].sessions.map((session, index) => (
                                                <div key={index} className='session_row'>
                                                    <div className='session_highlight'>{index + 1}</div>
                                                    <div className={`place_${session.place <= 3 ? 'top' : session.place <= 10 ? 'good' : 'normal'}`}>{session.place}</div>
                                                    <div className='session_highlight'>{session.kills}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Leaderboardjolavanille_arena_solo
